import { useEffect, useState } from 'react';
import { API_CONFIG } from '../lib/apiConfig';
import { fetchWithCache } from '../lib/fetchWithCache';
import {
  SpeciesDominantOverlayMeta,
  SpeciesOverlayVariant,
  SpeciesRichnessOverlayMeta,
} from '../lib/types';

type SpeciesMeta = SpeciesDominantOverlayMeta | SpeciesRichnessOverlayMeta;

interface UseSpeciesOverlayResult {
  url: string | null;
  bounds: [[number, number], [number, number]] | null;
  meta: SpeciesMeta | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Finds the precomputed species overlay PNG + geographic bounds for a given
 * (mill, drive_time, variant). Parameterized on variant so a single hook
 * serves both the dominant-species PNG and the richness-count PNG.
 *
 * Returns nulls (with no error) when `enabled=false` or `millCode=null` so
 * callers can unconditionally render and rely on the null check to skip.
 */
export const useSpeciesOverlay = (
  millCode: string | null,
  driveTimeMinutes: number,
  variant: SpeciesOverlayVariant,
  enabled: boolean = true,
): UseSpeciesOverlayResult => {
  const [bounds, setBounds] = useState<[[number, number], [number, number]] | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [meta, setMeta] = useState<SpeciesMeta | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setBounds(null);
      setUrl(null);
      setMeta(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const metaPath = variant === 'dominant'
      ? API_CONFIG.s3.paths.speciesOverlayDominantMeta(millCode, driveTimeMinutes)
      : API_CONFIG.s3.paths.speciesOverlayRichnessMeta(millCode, driveTimeMinutes);
    const pngPath = variant === 'dominant'
      ? API_CONFIG.s3.paths.speciesOverlayDominant(millCode, driveTimeMinutes)
      : API_CONFIG.s3.paths.speciesOverlayRichness(millCode, driveTimeMinutes);

    const metaUrl = `${API_CONFIG.s3.baseUrl}/${metaPath}`;
    const pngUrl = `${API_CONFIG.s3.baseUrl}/${pngPath}`;

    // Clear stale state before the new fetch so callers never see a
    // dominant-shape meta paired with a richness overlay URL (or vice versa)
    // during a variant switch — that mismatch crashed the legend renderer.
    setBounds(null);
    setUrl(null);
    setMeta(null);

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache<SpeciesMeta>(metaUrl, r => r.json())
      .then(parsed => {
        if (cancelled) return;
        setBounds(parsed.bounds);
        setMeta(parsed);
        setUrl(pngUrl);
        setIsLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setBounds(null);
        setMeta(null);
        setUrl(null);
        setError(`Failed to load overlay metadata: ${err.message}`);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTimeMinutes, variant, enabled]);

  return { url, bounds, meta, isLoading, error };
};
